<template>
  <div v-if="isSupportedPlatform">
    <div
      v-if="!isLoaded"
      ref="thumbnailContainer"
      class="relative cursor-pointer"
      :class="{ 'aspect-video': !video.thumbnail }"
      @click="onThumbnailClicked"
    >
      <fa-icon
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-brand size-8 md:size-14"
        :icon="faPlayCircle"
        aria-hidden="true"
      />
      <strapi-image v-if="video.thumbnail" :image="video.thumbnail" fluid />
    </div>

    <div
      class="video-player__wrapper relative overflow-hidden w-full"
      :class="{ 'video-player__wrapper--player-loaded': isLoaded }"
    >
      <lazy-media-gallery-video-youtube-video
        v-if="video.platform === YOUTUBE_VIDEO_PLATFORM"
        ref="videoComponent"
        :is-active="isActive"
        :video="video"
        :width="width"
        :height="height"
        @video-loaded="(value) => (isLoaded = value)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StrapiMediaGalleryVideoItem } from '~/apollo/types/types';
import { faPlayCircle } from '@fortawesome/pro-solid-svg-icons';
import { YOUTUBE_VIDEO_PLATFORM } from '~/composables/video/useYoutube';
import type { LazyMediaGalleryVideoYoutubeVideo } from '#build/components';

const props = withDefaults(
  defineProps<{
    isActive?: boolean;
    video: StrapiMediaGalleryVideoItem;
  }>(),
  {
    isActive: false,
  },
);

const thumbnailContainer = ref<HTMLElement>();
const { height, width } = useElementBounding(thumbnailContainer);
const isSupportedPlatform = computed(() =>
  [YOUTUBE_VIDEO_PLATFORM].includes(props.video.platform),
);

const videoComponent = ref<InstanceType<
  typeof LazyMediaGalleryVideoYoutubeVideo
> | null>(null);

const isLoaded = ref(false);

const onThumbnailClicked = async () => {
  await nextTick();
  if (videoComponent.value) {
    videoComponent.value.loadVideo();
  }
};
</script>

<style>
/* Unscoped — must style iframe inside child component */
.video-player__wrapper--player-loaded::after {
  padding-top: 56.25%;
  display: block;
  content: '';
}

.video-player__wrapper--player-loaded iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
